/**
 * Scenario Comparison Panel — Doc 17 §1.5
 *
 * Side-by-side scenario scores (feasibility, divergence penalty, risk penalty).
 * Data: plan_scenarios joined with scenario_scores.
 * Allows creating named scenarios and running the score engine on demand.
 */

import { useState } from 'react';
import { GitBranch, Plus, Loader2, X, ArrowLeftRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { createScenario } from '../../lib/planningService';
import type { PlanScenarioRow } from '../../lib/planningService';

// ─── Diff helpers ─────────────────────────────────────────────────────────────

interface ScoreDimension {
  label: string;
  key: string;
  higherIsBetter: boolean;
  color: string;
}

const SCORE_DIMENSIONS: ScoreDimension[] = [
  { label: 'Feasibility',        key: 'feasibility',        higherIsBetter: true,  color: 'bg-[var(--green)]' },
  { label: 'Divergence penalty', key: 'divergence_penalty', higherIsBetter: false, color: 'bg-[var(--mustard)]' },
  { label: 'Risk penalty',       key: 'risk_penalty',       higherIsBetter: false, color: 'bg-[var(--coral)]'   },
];

function getScoreMap(scenario: PlanScenarioRow): Record<string, number | null> {
  const scores = scenario.scores ?? [];
  return Object.fromEntries(SCORE_DIMENSIONS.map((d) => [
    d.key,
    scores.find((s) => s.score_type === d.key)?.value ?? null,
  ]));
}

function DeltaChip({ delta, higherIsBetter }: { delta: number; higherIsBetter: boolean }) {
  const isPositive = higherIsBetter ? delta > 0 : delta < 0;
  const isNeutral = Math.abs(delta) < 0.005;
  if (isNeutral) return <span className="inline-flex items-center text-xs text-[var(--text-ter)]"><Minus className="h-3 w-3" /></span>;
  const pct = Math.round(Math.abs(delta) * 100);
  const cls = isPositive ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]';
  const Icon = isPositive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" />
      {pct}%
    </span>
  );
}

// ─── Score bar ────────────────────────────────────────────────────────────────

function ScoreBar({ value, color }: { value: number; color: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-[var(--text-ter)] w-8 text-right">{pct}%</span>
    </div>
  );
}

// ─── Scenario card ────────────────────────────────────────────────────────────

function ScenarioCard({ scenario }: { scenario: PlanScenarioRow }) {
  const scores = scenario.scores ?? [];
  const feasibility = scores.find((s) => s.score_type === 'feasibility')?.value ?? null;
  const divergencePenalty = scores.find((s) => s.score_type === 'divergence_penalty')?.value ?? null;
  const riskPenalty = scores.find((s) => s.score_type === 'risk_penalty')?.value ?? null;
  const hasScores = feasibility !== null;

  return (
    <div className="bg-[var(--surface)] border border-[var(--rule)] p-4 space-y-3 min-w-[180px]">
      <div>
        <p className="text-sm font-medium text-[var(--text-pri)] truncate">{scenario.name}</p>
        <p className="text-xs text-[var(--text-ter)]">{new Date(scenario.created_at).toLocaleDateString()}</p>
      </div>

      {hasScores ? (
        <div className="space-y-2">
          <div>
            <p className="text-xs text-[var(--text-ter)] mb-1">Feasibility</p>
            <ScoreBar value={feasibility!} color="bg-[var(--green)]" />
          </div>
          <div>
            <p className="text-xs text-[var(--text-ter)] mb-1">Divergence penalty</p>
            <ScoreBar value={divergencePenalty!} color="bg-[var(--mustard)]" />
          </div>
          <div>
            <p className="text-xs text-[var(--text-ter)] mb-1">Risk penalty</p>
            <ScoreBar value={riskPenalty!} color="bg-[var(--coral)]" />
          </div>
          {feasibility !== null && divergencePenalty !== null && riskPenalty !== null && (
            <div className="pt-1 border-t border-[var(--rule)]">
              <p className="text-xs text-[var(--text-ter)] mb-1">Net score</p>
              <p className="text-lg font-semibold text-[var(--text-pri)]">
                {Math.round((feasibility - divergencePenalty * 0.5 - riskPenalty * 0.3) * 100)}
                <span className="text-xs font-normal text-[var(--text-ter)] ml-1">/100</span>
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="py-3 text-center">
          <p className="text-xs text-[var(--text-ter)]">No scores yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">Run the engine to score</p>
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface ScenarioComparisonPanelProps {
  planCycleId: string;
  scenarios: PlanScenarioRow[];
  onChanged: () => void;
}

type PanelView = 'compare' | 'diff';

export function ScenarioComparisonPanel({
  planCycleId,
  scenarios,
  onChanged,
}: ScenarioComparisonPanelProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [view, setView] = useState<PanelView>('compare');
  // Diff: pick a baseline and a comparison scenario
  const [baselineId, setBaselineId] = useState<string | null>(null);
  const [compareId, setCompareId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    const { error } = await createScenario(planCycleId, newName.trim());
    setCreating(false);
    if (error) { setCreateError(error); return; }
    setNewName('');
    setShowCreate(false);
    onChanged();
  };

  if (scenarios.length === 0 && !showCreate) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
            Scenarios
          </h2>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            <Plus className="h-3.5 w-3.5" />
            New scenario
          </button>
        </div>
        <p className="text-sm text-[var(--text-ter)]">
          No scenarios defined. Create scenarios to compare feasibility, risk, and divergence trade-offs side by side.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
          Scenarios
        </h2>
        <div className="flex items-center gap-2">
          {scenarios.length >= 2 && (
            <div className="flex border border-[var(--rule)] overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setView('compare')}
                className={`px-2.5 py-1 font-medium transition-colors ${view === 'compare' ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'}`}
              >
                Compare
              </button>
              <button
                type="button"
                onClick={() => setView('diff')}
                className={`px-2.5 py-1 font-medium transition-colors flex items-center gap-1 ${view === 'diff' ? 'bg-primary-50 text-primary-700' : 'text-[var(--text-ter)] hover:bg-[var(--surface)]'}`}
              >
                <ArrowLeftRight className="h-3 w-3" />
                Diff
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {showCreate ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showCreate ? 'Cancel' : 'New scenario'}
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Scenario name…"
            className="flex-1 border border-[var(--rule)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
            autoFocus
          />
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating || !newName.trim()}
            className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
          >
            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create
          </button>
          {createError && <p className="text-xs text-[var(--coral)]">{createError}</p>}
        </div>
      )}

      {/* Compare view — side-by-side cards */}
      {view === 'compare' && scenarios.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {scenarios.map((s) => (
            <ScenarioCard key={s.id} scenario={s} />
          ))}
        </div>
      )}

      {/* Diff view — pick baseline + comparison, show delta table */}
      {view === 'diff' && scenarios.length >= 2 && (
        <div className="space-y-3">
          {/* Scenario pickers */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--text-ter)] font-medium">Baseline</span>
              <select
                value={baselineId ?? ''}
                onChange={(e) => setBaselineId(e.target.value || null)}
                className="border border-[var(--rule)] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select…</option>
                {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <ArrowLeftRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--text-ter)] font-medium">Compare to</span>
              <select
                value={compareId ?? ''}
                onChange={(e) => setCompareId(e.target.value || null)}
                className="border border-[var(--rule)] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select…</option>
                {scenarios.filter((s) => s.id !== baselineId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Delta table */}
          {baselineId && compareId && (() => {
            const base = scenarios.find((s) => s.id === baselineId);
            const comp = scenarios.find((s) => s.id === compareId);
            if (!base || !comp) return null;
            const baseScores = getScoreMap(base);
            const compScores = getScoreMap(comp);
            const hasAnyScores = SCORE_DIMENSIONS.some((d) => baseScores[d.key] !== null || compScores[d.key] !== null);
            if (!hasAnyScores) {
              return <p className="text-xs text-[var(--text-ter)]">Neither scenario has scores yet — run the engine to compute.</p>;
            }
            return (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--text-ter)] border-b border-[var(--rule)]">
                    <th className="text-left py-1.5 font-medium">Metric</th>
                    <th className="text-right py-1.5 font-medium">{base.name}</th>
                    <th className="text-right py-1.5 font-medium">{comp.name}</th>
                    <th className="text-right py-1.5 font-medium">Delta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)]">
                  {SCORE_DIMENSIONS.map((dim) => {
                    const bv = baseScores[dim.key];
                    const cv = compScores[dim.key];
                    const delta = bv !== null && cv !== null ? cv - bv : null;
                    return (
                      <tr key={dim.key}>
                        <td className="py-1.5 text-[var(--text-sec)]">{dim.label}</td>
                        <td className="py-1.5 text-right text-[var(--text-sec)]">
                          {bv !== null ? `${Math.round(bv * 100)}%` : '—'}
                        </td>
                        <td className="py-1.5 text-right text-[var(--text-sec)]">
                          {cv !== null ? `${Math.round(cv * 100)}%` : '—'}
                        </td>
                        <td className="py-1.5 text-right">
                          {delta !== null
                            ? <DeltaChip delta={delta} higherIsBetter={dim.higherIsBetter} />
                            : <span className="text-[var(--text-ter)]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Net score row */}
                  {(() => {
                    const netBase = baseScores.feasibility !== null && baseScores.divergence_penalty !== null && baseScores.risk_penalty !== null
                      ? baseScores.feasibility! - baseScores.divergence_penalty! * 0.5 - baseScores.risk_penalty! * 0.3
                      : null;
                    const netComp = compScores.feasibility !== null && compScores.divergence_penalty !== null && compScores.risk_penalty !== null
                      ? compScores.feasibility! - compScores.divergence_penalty! * 0.5 - compScores.risk_penalty! * 0.3
                      : null;
                    const netDelta = netBase !== null && netComp !== null ? netComp - netBase : null;
                    return (
                      <tr className="border-t border-[var(--rule)] font-medium">
                        <td className="py-1.5 text-[var(--text-sec)]">Net score</td>
                        <td className="py-1.5 text-right text-[var(--text-pri)]">
                          {netBase !== null ? Math.round(netBase * 100) : '—'}
                        </td>
                        <td className="py-1.5 text-right text-[var(--text-pri)]">
                          {netComp !== null ? Math.round(netComp * 100) : '—'}
                        </td>
                        <td className="py-1.5 text-right">
                          {netDelta !== null
                            ? <DeltaChip delta={netDelta} higherIsBetter={true} />
                            : <span className="text-[var(--text-ter)]">—</span>}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}

      <p className="text-xs text-[var(--text-ter)]">
        Run the Planning Engine on a plan version to compute and persist scores for a scenario.
      </p>
    </div>
  );
}
